import React from "react";
import Topbar from "../../components/topbar";
import { useFeatures } from "../../providers/features-provider";
import { useMediaQuery, useTheme, Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, Divider, IconButton, Tooltip } from "@mui/material";
import SectionWrapper from "../../components/section";
import Toggle from "../../components/ui/toggle";
import SettingsIcon from '@mui/icons-material/Settings';
import GSTCategoryModal, { GSTCategory } from "../../components/popup/gst-category-modal";
import GSTCycleModal, { ReportingType } from "../../components/popup/gst-cycle-modal";
import { useUser } from "../../providers/user-provider";
import { usePermissions } from "../../providers/permissions-provider";
import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const GST_FIELD_LABELS: Record<string, string> = {
    G1: 'Total Sales (G1)', G2: 'Export Sales (G2)', G3: 'Other GST-Free Sales (G3)',
    G10: 'Capital Purchases (G10)', G11: 'Non-capital Purchases (G11)',
    W1: 'Total Wages and Salary (W1)', W2: 'Amount Withheld from W1 (W2)',
    W3: 'Other Amounts Withheld (W3)', W4: 'Withheld No ABN (W4)',
    '1A': 'GST on Sales (1A)', '1B': 'GST on Purchases (1B)',
    '1C': 'Wine Equalisation Tax (1C)', '1D': 'Wine Equalisation Tax Refundable (1D)',
    '1E': 'Luxury Car Tax (1E)', '1F': 'Luxury Car Tax Refundable (1F)',
    '4': 'PAYG Tax Withheld (4)', '5A': 'PAYG Income Tax Installment (5A)',
    '5B': 'Credit from PAYG ITI Variation (5B)', '6A': 'FBT Installment (6A)',
    '6B': 'Credit from FBT Installment Variation (6B)',
    '7': 'Deferred Company Installment (7)', '7C': 'Fuel Tax Credit Over Claim (7C)',
    '7D': 'Fuel Tax Credit (7D)',
};

// ─── Shared ───────────────────────────────────────────────────────────────────

interface GSTRowProps {
    label: string;
    code: string;
    value: string;
    showGear?: boolean;
    readOnly?: boolean;
    onGearClick?: () => void;
}

const GearIcon: React.FC<{ onGearClick?: () => void; color: string }> = ({ onGearClick, color }) => {
    const icon = (
        <SettingsIcon
            onClick={onGearClick}
            sx={{ fontSize: 18, color: onGearClick ? color : 'var(--color-text-muted)', cursor: onGearClick ? 'pointer' : 'not-allowed', opacity: onGearClick ? 1 : 0.5 }}
        />
    );
    return onGearClick ? icon : <Tooltip title="You don't have permission to edit this"><span>{icon}</span></Tooltip>;
};

const GSTRow: React.FC<GSTRowProps> = ({ label, code, value, showGear = true, readOnly = false, onGearClick }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Typography sx={{ flex: 1, fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'right', pr: 2 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)', minWidth: 28, textAlign: 'right' }}>{code}</Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mx: 0.5, fontWeight: 600 }}>$</Typography>
                <TextField size="small" value={value} disabled={readOnly}
                    sx={{ width: { xs: 100, sm: 120 }, background: readOnly ? 'var(--color-container-background)' : '#fff' }}
                    slotProps={{ htmlInput: { style: { fontSize: 13, padding: '4px 8px' } } }}
                />
                <Box sx={{ width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {showGear && <GearIcon onGearClick={onGearClick} color={theme.palette.primary.main} />}
                </Box>
            </Box>
        </Box>
    );
};

const ClassicRow: React.FC<GSTRowProps> = ({ label, code, value, showGear = true, readOnly = false, onGearClick }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.65 }}>
        <Typography sx={{ flex: 1, fontSize: 13, color: 'var(--color-default-text)', textAlign: 'right', pr: 2 }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-default-text)', minWidth: 26, textAlign: 'right' }}>{code}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)', mx: 0.25 }}>$</Typography>
            <TextField size="small" value={value} disabled={readOnly}
                sx={{ width: { xs: 110, sm: 140 }, background: readOnly ? 'var(--color-container-background)' : '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' } }}
                slotProps={{ htmlInput: { style: { fontSize: 13, padding: '4px 8px', background: readOnly ? 'var(--color-container-background)' : '#fff' } } }}
            />
            <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {showGear && <GearIcon onGearClick={onGearClick} color="var(--color-primary)" />}
            </Box>
        </Box>
    </Box>
);

const ClassicDivider: React.FC = () => (
    <Box sx={{ borderTop: '2px solid #333', my: 0.75, ml: '45%' }} />
);

// ─── Main ─────────────────────────────────────────────────────────────────────

const GST: React.FC = () => {
    const { isEnabled } = useFeatures();
    const { user } = useUser();
    const { can } = usePermissions();
    const isReadOnly = !can('summaries_gst', 'update');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [classicView, setClassicView] = React.useState(true);
    const [activeQuarter, setActiveQuarter] = React.useState('q1');
    const [activeMonth, setActiveMonth] = React.useState('m1');

    // ── GST tax definition (used to derive whether G1-3/G10-11 transactions
    // actually have GST applied, rather than relying on a manual guess) ──────
    const [fetchTaxDefinitions, setFetchTaxDefinitionsConfig] = useApi<any>();

    React.useEffect(() => {
        if (!user?.entityId) return;
        setFetchTaxDefinitionsConfig(buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: user.entityId }]));
    }, [user?.entityId]);

    const gstTaxDef = React.useMemo(() => {
        const list: any[] = Array.isArray(fetchTaxDefinitions.data) ? fetchTaxDefinitions.data : [];
        return list.find(t => (t.name || '').trim().toLowerCase() === 'gst' && t.isActive !== false && t.isEnabled !== false);
    }, [fetchTaxDefinitions.data]);

    const gstTaxId: number | undefined = gstTaxDef?.id;
    const gstRate: number = gstTaxDef ? (Number(gstTaxDef.rate) || 0) : 10;

    // ── Reporting cycle ──────────────────────────────────────────────────────
    // Fiscal year start month is configurable per entity (Company Settings →
    // Localisation), defaulting to July (AU convention) until settings load.
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = React.useState(7);
    // Projection Horizon (e.g. "2 years", "30 days") governs how far into the
    // future the year list extends, per the GST Reporting Cycle spec.
    const [projectionHorizon, setProjectionHorizon] = React.useState('1 years');
    const [fetchSettings, setFetchSettings] = useApi<any>();

    React.useEffect(() => {
        if (user?.entityId) {
            setFetchSettings(buildApiRequestConfig(routes.companySettings.fetchById, [
                { param: 'id', value: user.entityId },
            ]));
        }
    }, [user?.entityId]);

    React.useEffect(() => {
        const month = fetchSettings.data?.settings?.fiscalYearStartMonth;
        if (month) setFiscalYearStartMonth(Number(month));
        const horizon = fetchSettings.data?.settings?.projectionHorizon;
        if (horizon) setProjectionHorizon(horizon);
    }, [fetchSettings.data]);

    const now = new Date();
    const fyStartYear = (now.getMonth() + 1) >= fiscalYearStartMonth ? now.getFullYear() : now.getFullYear() - 1;

    // Converts a "<N> <unit>" projection horizon into a whole number of years
    // to look ahead, rounding up so the furthest horizon date is always covered.
    const projectionHorizonYears = React.useMemo(() => {
        const [valueStr, unit] = (projectionHorizon || '').split(' ');
        const value = parseInt(valueStr, 10) || 0;
        // Singular ("1 year") or plural.
        switch ((unit || '').toLowerCase().replace(/s$/, '')) {
            case 'year': return Math.max(1, value);
            case 'month': return Math.max(1, Math.ceil(value / 12));
            case 'week': return Math.max(1, Math.ceil(value / 52));
            case 'day': return Math.max(1, Math.ceil(value / 365));
            default: return 1;
        }
    }, [projectionHorizon]);

    // Reporting cycle years should cover the entity's actual transaction history,
    // not just a fixed window around today
    const [txDateRange, setTxDateRange] = React.useState<{ earliest?: string; latest?: string }>({});
    const cycleYears = React.useMemo(() => {
        const fyYearOf = (d: Date) => (d.getMonth() + 1) >= fiscalYearStartMonth ? d.getFullYear() : d.getFullYear() - 1;
        const earliestFy = txDateRange.earliest ? fyYearOf(new Date(txDateRange.earliest)) : fyStartYear;
        const latestFy = txDateRange.latest ? fyYearOf(new Date(txDateRange.latest)) : fyStartYear;
        // Furthest year is the current fiscal year plus the configured Projection Horizon
        const maxYear = Math.max(latestFy, fyStartYear) + projectionHorizonYears;
        const minYear = Math.min(earliestFy, fyStartYear);
        const years: number[] = [];
        for (let y = maxYear; y >= minYear; y--) years.push(y);
        return years;
    }, [txDateRange, fyStartYear, fiscalYearStartMonth, projectionHorizonYears]);

    const [cycles, setCycles] = React.useState<Record<number, ReportingType>>({});
    const [cycleAnchor, setCycleAnchor] = React.useState<HTMLElement | null>(null);
    const [fetchCycles, setFetchCyclesConfig] = useApi<any>();
    const [saveCyclesResult, setSaveCyclesConfig] = useApi<any>();

    // Year is freely selectable by the user; it only tracks the current fiscal
    // year automatically until the user picks one themselves
    const [activeYear, setActiveYear] = React.useState<number>(fyStartYear);
    const [yearManuallySelected, setYearManuallySelected] = React.useState(false);
    React.useEffect(() => {
        if (!yearManuallySelected) setActiveYear(fyStartYear);
    }, [fyStartYear, yearManuallySelected]);

    const parseCycles = (raw: Record<string, ReportingType> | undefined): Record<number, ReportingType> => {
        const parsed: Record<number, ReportingType> = {};
        Object.entries(raw || {}).forEach(([year, type]) => { parsed[Number(year)] = type; });
        return parsed;
    };

    React.useEffect(() => {
        if (!user?.entityId) return;
        setFetchCyclesConfig(buildApiRequestConfig(routes.gstReportingCycles.fetchByEntity, [{ param: 'entityId', value: user.entityId }]));
    }, [user?.entityId]);

    React.useEffect(() => {
        if (fetchCycles.data?.success) setCycles(parseCycles(fetchCycles.data.cycles));
    }, [fetchCycles.data]);

    const handleSaveCycles = (draft: Record<number, ReportingType>) => {
        if (!user?.entityId) return;
        setSaveCyclesConfig({
            ...buildApiRequestConfig(routes.gstReportingCycles.save, [{ param: 'entityId', value: user.entityId }]),
            body: { cycles: draft },
        });
    };

    React.useEffect(() => {
        if (saveCyclesResult.data?.success) setCycles(parseCycles(saveCyclesResult.data.cycles));
    }, [saveCyclesResult.data]);

    const activeCycle: ReportingType = cycles[activeYear] ?? '';
    const cycleLabel = activeCycle === 'quarter' ? 'QUARTER' : activeCycle === 'annual' ? 'ANNUAL' : activeCycle === 'month' ? 'MONTH' : 'SELECT';
    const cycleDisplayName = activeCycle === 'quarter' ? 'Quarter' : activeCycle === 'annual' ? 'Annual' : activeCycle === 'month' ? 'Month' : 'Cycle';
    // Same color mapping as the Recurrence badge on the Provisions page
    // (monthly/quarterly/yearly), so cycle type reads consistently across the app.
    const cycleColor = activeCycle === 'quarter' ? '#9C27B0' : activeCycle === 'annual' ? '#4CAF50' : activeCycle === 'month' ? 'var(--color-primary)' : '#c7c7c7';

    // Calendar {year, month} for the Nth month (0-11) of the active fiscal year.
    const fyMonth = (offset: number) => {
        let month = fiscalYearStartMonth + offset;
        let year = activeYear;
        if (month > 12) { month -= 12; year += 1; }
        return { year, month };
    };
    const lastDayOf = (year: number, month: number) => new Date(year, month, 0).getDate();
    const pad = (n: number) => String(n).padStart(2, '0');

    const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    const quarters = [0, 3, 6, 9].map((startOffset, i) => {
        const start = fyMonth(startOffset);
        const end = fyMonth(startOffset + 2);
        return {
            key: `q${i + 1}`,
            label: `${MONTH_NAMES[start.month - 1]} ${String(start.year).slice(2)} - ${MONTH_NAMES[end.month - 1]} ${String(end.year).slice(2)}`,
        };
    });

    const months = Array.from({ length: 12 }, (_, i) => {
        const { year, month } = fyMonth(i);
        return { key: `m${i + 1}`, label: `${MONTH_NAMES[month - 1]} ${String(year).slice(2)}`, month, year };
    });

    const annualLabel = (() => {
        const start = fyMonth(0);
        const end = fyMonth(11);
        return `${MONTH_NAMES[start.month - 1]} ${String(start.year).slice(2)} - ${MONTH_NAMES[end.month - 1]} ${String(end.year).slice(2)}`;
    })();

    const dateRange = React.useMemo(() => {
        if (activeCycle === 'quarter') {
            const qIndex = Number(activeQuarter.replace('q', '')) - 1;
            const start = fyMonth(qIndex * 3);
            const end = fyMonth(qIndex * 3 + 2);
            return {
                start: `${start.year}-${pad(start.month)}-01`,
                end: `${end.year}-${pad(end.month)}-${pad(lastDayOf(end.year, end.month))}`,
            };
        }
        if (activeCycle === 'annual') {
            const start = fyMonth(0);
            const end = fyMonth(11);
            return {
                start: `${start.year}-${pad(start.month)}-01`,
                end: `${end.year}-${pad(end.month)}-${pad(lastDayOf(end.year, end.month))}`,
            };
        }
        if (activeCycle === 'month') {
            const def = months.find(m => m.key === activeMonth) ?? months[0];
            return {
                start: `${def.year}-${pad(def.month)}-01`,
                end: `${def.year}-${pad(def.month)}-${pad(lastDayOf(def.year, def.month))}`,
            };
        }
        return null;
    }, [activeCycle, activeYear, activeQuarter, activeMonth, fiscalYearStartMonth]);

    // ── Category modal ───────────────────────────────────────────────────────
    const [modalField, setModalField] = React.useState<{ code: string; label: string } | null>(null);
    const [mappings, setMappings] = React.useState<Record<string, number[]>>({});
    const [categories, setCategories] = React.useState<GSTCategory[]>([]);
    const [transactions, setTransactions] = React.useState<any[]>([]);

    const [fetchCategories, setFetchCategories] = useApi<any>();
    const [fetchTx, setFetchTx] = useApi<any>();
    const [fetchFieldMappings, setFetchFieldMappingsConfig] = useApi<any>();
    const [saveFieldMappingResult, setSaveFieldMappingConfig] = useApi<any>();

    React.useEffect(() => {
        if (user?.entityId) {
            setFetchCategories(buildApiRequestConfig(routes.categories.fetchByEntity, [
                { param: 'entityId', value: user.entityId },
            ]));
            setFetchFieldMappingsConfig(buildApiRequestConfig(routes.gstFieldMappings.fetchByEntity, [
                { param: 'entityId', value: user.entityId },
            ]));
        }
    }, [user?.entityId]);

    React.useEffect(() => {
        if (fetchFieldMappings.data?.success) setMappings(fetchFieldMappings.data.mappings ?? {});
    }, [fetchFieldMappings.data]);

    React.useEffect(() => {
        if (saveFieldMappingResult.data?.success) setMappings(saveFieldMappingResult.data.mappings ?? {});
    }, [saveFieldMappingResult.data]);

    React.useEffect(() => {
        const data = fetchCategories.data;
        if (!data) return;
        const list: any[] = Array.isArray(data) ? data : data.categories ?? [];
        const flat: GSTCategory[] = [];
        const flatten = (items: any[]) => {
            for (const item of items) {
                flat.push({ id: item.id, name: item.name, code: item.code });
                if (item.children?.length) flatten(item.children);
            }
        };
        flatten(list);
        setCategories(flat);
    }, [fetchCategories.data]);

    // Every transaction, once - the endpoint returns the whole history whatever
    // startDate/endDate say (date filtering moved client-side), so the period
    // is applied below rather than by refetching per period.
    React.useEffect(() => {
        if (!user?.entityId) return;
        const config = buildApiRequestConfig(routes.transaction.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
        ]);
        setFetchTx({ ...config, body: { showReal: true, showRecurring: true, showOneTime: true } });
    }, [user?.entityId]);

    React.useEffect(() => {
        const data = fetchTx.data;
        if (!data) return;
        setTransactions(data.transactions ?? []);
        setTxDateRange({ earliest: data.status?.earliestTransaction, latest: data.status?.latestTransaction });
    }, [fetchTx.data]);

    // The selected period's transactions, by local calendar date - the same
    // set the GST provision is built from (gst-calculation-service.js), which
    // also leaves out $0 provisions.
    const periodTransactions = React.useMemo(() => {
        if (!dateRange) return [];
        const dayKey = (value: string) => {
            const d = new Date(value);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        };
        return transactions.filter(t => {
            if (t.type === 'provision' && Number(t.amount) === 0) return false;
            const day = dayKey(t.date);
            return day >= dateRange.start && day <= dateRange.end;
        });
    }, [transactions, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const calcField = React.useCallback((code: string): string => {
        const ids = mappings[code] ?? [];
        if (!ids.length) return '';
        // A category is selected for this field, so compute even if it currently
        // has no matching transactions — show 0.00 rather than leaving it blank.
        // BAS boxes are amounts, not directions - purchases and withholdings are
        // negative bank amounts, so the net is shown as its size (refunds within
        // a category still net out). Same as gst-calculation-service.js.
        const total = periodTransactions
            .filter(t => ids.includes(t.categoryId))
            .reduce((sum: number, t: any) => sum + (Number(t.amount) || 0), 0);
        return Math.abs(total).toFixed(2);
    }, [mappings, periodTransactions]);

    // Transactions actually carrying the entity's "GST" tax, restricted to the
    // categories mapped to the given field codes. G1/G2/G3/G10/G11 themselves
    // stay full, unfiltered sums via calcField above — only 1A/1B (and the
    // disabled radio display) are derived from this GST-tax-applied subset.
    const gstTransactionsForFields = React.useCallback((codes: string[]) => {
        const ids = codes.flatMap(code => mappings[code] ?? []);
        if (!ids.length || gstTaxId === undefined) return [];
        return periodTransactions.filter(t => {
            if (!ids.includes(t.categoryId)) return false;
            const applied: number[] = Array.isArray(t.appliedTaxIds) ? t.appliedTaxIds : [];
            return applied.includes(gstTaxId);
        });
    }, [mappings, periodTransactions, gstTaxId]);

    // Sum of the GST component of just the transactions that actually have GST
    // tax applied within the given field codes' mapped categories (transactions
    // without GST applied contribute nothing). Bank amounts include GST, so the
    // GST is extracted from them (amount * rate / (100 + rate)) - the same as the
    // GST provision (gst-calculation-service.js), so the page and the provision agree.
    const calcGstPortion = React.useCallback((codes: string[]): number => {
        const total = gstTransactionsForFields(codes)
            .reduce((sum: number, t: any) => sum + (Number(t.amount) || 0) * gstRate / (100 + gstRate), 0);
        return parseFloat(Math.abs(total).toFixed(2));
    }, [gstTransactionsForFields, gstRate]);

    // Purely informational now (radio is disabled): reflects whether any of the
    // mapped transactions for this period actually have GST applied.
    const g1HasGstApplied = gstTransactionsForFields(['G1', 'G2', 'G3']).length > 0;
    const purchasesHaveGstApplied = gstTransactionsForFields(['G10', 'G11']).length > 0;

    const handleSave = (categoryIds: number[]) => {
        if (!modalField || !user?.entityId) return;
        setSaveFieldMappingConfig({
            ...buildApiRequestConfig(routes.gstFieldMappings.save, [{ param: 'entityId', value: user.entityId }]),
            body: { fieldCode: modalField.code, categoryIds },
        });
        setModalField(null);
    };

    const gear = (code: string, label: string) => isReadOnly ? undefined : () => setModalField({ code, label });

    // ── Computed totals ──────────────────────────────────────────────────────
    const toNum = (v: string) => parseFloat(v) || 0;
    const totalG1 = toNum(calcField('G1')) + toNum(calcField('G2')) + toNum(calcField('G3'));
    const total1A = calcGstPortion(['G1', 'G2', 'G3']);
    const totalG10G11 = toNum(calcField('G10')) + toNum(calcField('G11'));
    const total1B = calcGstPortion(['G10', 'G11']);
    const w5 = toNum(calcField('W2')) + toNum(calcField('W4')) + toNum(calcField('W3'));
    const total8A = total1A + toNum(calcField('1C')) + toNum(calcField('1E')) + toNum(calcField('4')) + toNum(calcField('5A')) + toNum(calcField('6A')) + toNum(calcField('7')) + toNum(calcField('7C'));
    const total8B = total1B + toNum(calcField('1D')) + toNum(calcField('1F')) + toNum(calcField('5B')) + toNum(calcField('6B')) + toNum(calcField('7D'));
    const field9 = total8A >= total8B ? total8A - total8B : total8B - total8A;
    // Auto-derived from 8A/8B — not a manual choice.
    const is8AMoreThan8B = total8A >= total8B;
    const fmt = (n: number) => n !== 0 ? n.toFixed(2) : '';
    // 1A/1B: once the fields they depend on are mapped to a category, always show
    // the computed amount (including 0.00 when nothing has GST applied yet).
    const fieldsConfigured = (codes: string[]) => codes.some(code => (mappings[code] ?? []).length > 0);
    const fmtGst = (n: number, codes: string[]) => fieldsConfigured(codes) ? n.toFixed(2) : fmt(n);

    React.useEffect(() => { document.title = "FineAnts | GST"; }, []);

    const R = classicView ? ClassicRow : GSTRow;

    // ── GST Quarter ──────────────────────────────────────────────────────────
    const gstQuarter = <>
        <R label="Total Sales" code="G1" value={fmtGst(totalG1, ['G1', 'G2', 'G3'])} readOnly onGearClick={gear('G1', 'Total Sales')} />
        <Box sx={{ display: 'flex', alignItems: 'center', py: classicView ? 0.65 : 0.5 }}>
            <Typography sx={{ flex: 1, fontSize: 13, color: classicView ? 'var(--color-default-text)' : 'var(--color-text-muted)', textAlign: 'right', pr: 2 }}>
                Does G1 Include GST?
            </Typography>
            <RadioGroup row value={g1HasGstApplied ? 'yes' : 'no'}>
                <FormControlLabel value="yes" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>Yes</Typography>} />
                <FormControlLabel value="no" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>No</Typography>} />
            </RadioGroup>
            <Box sx={{ width: classicView ? 22 : 26 }} />
        </Box>
        <R label="Export Sales" code="G2" value={calcField('G2')} readOnly onGearClick={gear('G2', 'Export Sales')} />
        <R label="Other GST-Free Sales" code="G3" value={calcField('G3')} readOnly onGearClick={gear('G3', 'Other GST-Free Sales')} />
        <R label="Capital purchases" code="G10" value={calcField('G10')} readOnly onGearClick={gear('G10', 'Capital Purchases')} />
        <R label="Non-capital purchases" code="G11" value={calcField('G11')} readOnly onGearClick={gear('G11', 'Non-capital Purchases')} />
        {/* <Box sx={{ display: 'flex', alignItems: 'center', py: classicView ? 0.65 : 0.5 }}>
            <Typography sx={{ flex: 1, fontSize: 13, color: classicView ? 'var(--color-default-text)' : 'var(--color-text-muted)', textAlign: 'right', pr: 2 }}>
                Does G10/G11 Include GST?
            </Typography>
            <RadioGroup row value={purchasesHaveGstApplied ? 'yes' : 'no'}>
                <FormControlLabel value="yes" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>Yes</Typography>} />
                <FormControlLabel value="no" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>No</Typography>} />
            </RadioGroup>
            <Box sx={{ width: classicView ? 22 : 26 }} />
        </Box> */}
    </>;

    // ── PAYG ─────────────────────────────────────────────────────────────────
    const payg = <>
        <R label="Total Wages and Salary" code="W1" value={calcField('W1')} readOnly onGearClick={gear('W1', 'Total Wages and Salary')} />
        <R label="Amount Withheld from W1" code="W2" value={calcField('W2')} readOnly onGearClick={gear('W2', 'Amount Withheld from W1')} />
        <R label="Withheld No ABN" code="W4" value={calcField('W4')} readOnly onGearClick={gear('W4', 'Withheld No ABN')} />
        <R label="Other Amounts Withheld" code="W3" value={calcField('W3')} readOnly onGearClick={gear('W3', 'Other Amounts Withheld')} />
        {classicView ? <ClassicDivider /> : <Divider sx={{ my: 1 }} />}
        <R label="Total Withheld W2+W4+W3" code="W5" value={fmtGst(w5, ['W2', 'W4', 'W3'])} readOnly showGear={false} />
    </>;

    // ── Owe ATO ───────────────────────────────────────────────────────────────
    const oweATO = <>
        <R label="GST on Sales" code="1A" value={fmtGst(total1A, ['G1', 'G2', 'G3'])} readOnly showGear={false} />
        <R label="Wine Equalisation Tax" code="1C" value={calcField('1C')} readOnly onGearClick={gear('1C', 'Wine Equalisation Tax')} />
        <R label="Luxury Car Tax" code="1E" value={calcField('1E')} readOnly onGearClick={gear('1E', 'Luxury Car Tax')} />
        <R label="PAYG tax withheld" code="4" value={calcField('4')} readOnly onGearClick={gear('4', 'PAYG Tax Withheld')} />
        <R label="PAYG income tax installment" code="5A" value={calcField('5A')} readOnly onGearClick={gear('5A', 'PAYG Income Tax Installment')} />
        <R label="FBT Installment" code="6A" value={calcField('6A')} readOnly onGearClick={gear('6A', 'FBT Installment')} />
        <R label="Deferred company Installment" code="7" value={calcField('7')} readOnly onGearClick={gear('7', 'Deferred Company Installment')} />
        <R label="Fuel Tax Credit Over Claim" code="7C" value={calcField('7C')} readOnly onGearClick={gear('7C', 'Fuel Tax Credit Over Claim')} />
        {classicView ? <ClassicDivider /> : <Divider sx={{ my: 1 }} />}
        <R label="Total" code="8A" value={fmtGst(total8A, ['G1', 'G2', 'G3', '1C', '1E', '4', '5A', '6A', '7', '7C'])} readOnly showGear={false} />
    </>;

    // ── ATO Owes ──────────────────────────────────────────────────────────────
    const atoOwes = <>
        <R label="GST on purchases" code="1B" value={fmtGst(total1B, ['G10', 'G11'])} readOnly showGear={false} />
        <R label="Wine Equalisation Tax refundable" code="1D" value={calcField('1D')} readOnly onGearClick={gear('1D', 'Wine Equalisation Tax Refundable')} />
        <R label="Luxury Car Tax Refundable" code="1F" value={calcField('1F')} readOnly onGearClick={gear('1F', 'Luxury Car Tax Refundable')} />
        <R label="Credit from PAYG income tax installment variation" code="5B" value={calcField('5B')} readOnly onGearClick={gear('5B', 'Credit from PAYG ITI Variation')} />
        <R label="Credit from FBT Installment variation" code="6B" value={calcField('6B')} readOnly onGearClick={gear('6B', 'Credit from FBT Installment Variation')} />
        <R label="Fuel Tax Credit" code="7D" value={calcField('7D')} readOnly onGearClick={gear('7D', 'Fuel Tax Credit')} />
        {classicView ? <ClassicDivider /> : <Divider sx={{ my: 1 }} />}
        <R label="Total" code="8B" value={fmtGst(total8B, ['G10', 'G11', '1D', '1F', '5B', '6B', '7D'])} readOnly showGear={false} />
    </>;

    // ── Payment ───────────────────────────────────────────────────────────────
    // Stacks on narrow screens - side by side, the question, its answers and the
    // amount made the page wider than a phone, which zoomed the whole page out.
    const payment = (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 4 }, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 0.5, md: 2 }, flexDirection: { xs: 'column', md: 'row' } }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)', whiteSpace: 'nowrap' }}>Is 8A more than 8B?</Typography>
                <RadioGroup value={is8AMoreThan8B ? 'yes' : 'no'}>
                    <FormControlLabel value="yes" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>Yes. 8A - 8B is shown at 9 and is payable to the ATO.</Typography>} />
                    <FormControlLabel value="no" control={<Radio size="small" disabled />} label={<Typography fontSize={13}>No. 8B - 8A is shown at 9 and is refundable to you.</Typography>} />
                </RadioGroup>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#444', whiteSpace: 'nowrap' }}>Your Payment Due Amount</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>9 $</Typography>
                    <TextField size="small" value={fmtGst(field9, ['G1', 'G2', 'G3', '1C', '1E', '4', '5A', '6A', '7', '7C', 'G10', 'G11', '1D', '1F', '5B', '6B', '7D'])} disabled sx={{ width: 120 }}
                        slotProps={{ htmlInput: { style: { fontSize: 13, padding: '4px 8px' } } }}
                    />
                </Box>
            </Box>
        </Box>
    );

    if (!isEnabled('gst')) {
        return (
            <div style={{ width: '100%' }}>
                <Topbar title="GST" />
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">GST reporting is not available in your region.</Typography>
                </Box>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <GSTCycleModal
                open={!!cycleAnchor}
                anchorEl={cycleAnchor}
                cycles={cycles}
                years={cycleYears}
                activeYear={activeYear}
                onClose={() => setCycleAnchor(null)}
                onSave={saved => { handleSaveCycles(saved); setCycleAnchor(null); }}
                onYearClick={year => { setActiveYear(year); setYearManuallySelected(true); setCycleAnchor(null); }}
            />
            <GSTCategoryModal
                open={!!modalField}
                onClose={() => setModalField(null)}
                onSave={handleSave}
                fieldLabel={modalField?.label ?? ''}
                fieldCode={modalField?.code ?? ''}
                categories={categories}
                currentIds={modalField ? (mappings[modalField.code] ?? []) : []}
                mappings={mappings}
                fieldLabels={GST_FIELD_LABELS}
            />

            <Topbar isMobile={isMobile}>
                <Box display="flex" alignItems="center" gap={0.75} pl={isMobile ? 0 : 3}>
                    <Typography fontWeight={isMobile ? 600 : 500} variant="h6" sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        GST
                    </Typography>
                    <Tooltip title={isReadOnly ? "You don't have permission to edit this" : ''}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={isReadOnly}
                                onClick={e => setCycleAnchor(prev => prev ? null : e.currentTarget as HTMLElement)}
                                sx={{ color: theme.palette.primary.main, p: 0.5 }}
                            >
                                <SettingsIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {activeCycle && (
                        <Box sx={{ px: 1, py: 0.25, background: cycleColor, color: '#fff', borderRadius: '12px', fontSize: 12, fontWeight: 600, userSelect: 'none', letterSpacing: 1, whiteSpace: 'nowrap' }}>
                            {cycleLabel}
                        </Box>
                    )}
                </Box>
                <Box display="flex" alignItems="center" width="100%" pr={1} gap={1.5} ml={2}>
                    {activeCycle === 'annual' && (
                        <Box
                            sx={{
                                px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600,
                                color: '#fff', background: theme.palette.primary.main,
                                borderRadius: 1, border: `1px solid ${theme.palette.primary.main}`,
                                whiteSpace: 'nowrap', userSelect: 'none',
                            }}
                        >
                            {annualLabel}
                        </Box>
                    )}
                    {activeCycle === 'quarter' && (
                        <Box display="flex" gap={0.75}>
                            {quarters.map(q => (
                                <Box
                                    key={q.key}
                                    onClick={() => setActiveQuarter(q.key)}
                                    sx={{
                                        px: 1.5, py: 0.5, cursor: 'pointer', fontSize: 12,
                                        fontWeight: activeQuarter === q.key ? 600 : 400,
                                        color: activeQuarter === q.key ? '#fff' : 'var(--color-text-muted)',
                                        background: activeQuarter === q.key ? theme.palette.primary.main : 'transparent',
                                        borderRadius: 1,
                                        border: `1px solid ${activeQuarter === q.key ? theme.palette.primary.main : '#ccc'}`,
                                        whiteSpace: 'nowrap', userSelect: 'none', transition: 'all 0.15s',
                                    }}
                                >
                                    {q.label}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {activeCycle === 'month' && (
                        <Box display="flex" gap={0.75} sx={{ overflowX: 'auto' }}>
                            {months.map(m => (
                                <Box
                                    key={m.key}
                                    onClick={() => setActiveMonth(m.key)}
                                    sx={{
                                        px: 1.5, py: 0.5, cursor: 'pointer', fontSize: 12,
                                        fontWeight: activeMonth === m.key ? 600 : 400,
                                        color: activeMonth === m.key ? '#fff' : 'var(--color-text-muted)',
                                        background: activeMonth === m.key ? theme.palette.primary.main : 'transparent',
                                        borderRadius: 1,
                                        border: `1px solid ${activeMonth === m.key ? theme.palette.primary.main : '#ccc'}`,
                                        whiteSpace: 'nowrap', userSelect: 'none', transition: 'all 0.15s',
                                    }}
                                >
                                    {m.label}
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={1} ml="auto">
                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Classic view</Typography>
                        <Toggle checked={classicView} onChange={setClassicView} size="small" />
                    </Box>
                </Box>
            </Topbar>

            {classicView ? (
                <Box sx={{ padding: isMobile ? 0.5 : 1 }}>
                    <SectionWrapper>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 1.5, mt: 1 }}>
                                <ClassicBox title={`GST - ${cycleDisplayName}`} content={gstQuarter} />
                                <ClassicBox title="PAYG Tax Withheld" content={payg} />
                                <ClassicBox title="Summary - Amounts you owe the ATO" content={oweATO} />
                                <ClassicBox title="Summary - Amounts the ATO owes you" content={atoOwes} />
                            </Box>
                            <ClassicBox title="Payment or Refund?" content={payment} />
                        </Box>
                    </SectionWrapper>
                </Box>
            ) : (
                <Box sx={{ padding: isMobile ? 0.5 : 1, display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 1 }}>
                    <SectionWrapper title={`GST - ${cycleDisplayName}`}><Box sx={{ mt: 1 }}>{gstQuarter}</Box></SectionWrapper>
                    <SectionWrapper title="PAYG Tax Withheld"><Box sx={{ mt: 1 }}>{payg}</Box></SectionWrapper>
                    <SectionWrapper title="Summary - Amounts you owe the ATO"><Box sx={{ mt: 1 }}>{oweATO}</Box></SectionWrapper>
                    <SectionWrapper title="Summary - Amounts the ATO owes you"><Box sx={{ mt: 1 }}>{atoOwes}</Box></SectionWrapper>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <SectionWrapper title="Payment or Refund?"><Box sx={{ mt: 1 }}>{payment}</Box></SectionWrapper>
                    </Box>
                </Box>
            )}
        </div>
    );
};

interface ClassicBoxProps { title: string; content: React.ReactNode; }

const ClassicBox: React.FC<ClassicBoxProps> = ({ title, content }) => (
    <Box sx={{ boxSizing: 'border-box', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--color-primary)' }}>{title}</Typography>
        <Box sx={{ border: '1.5px solid var(--color-primary)', p: 2, borderRadius: 1, height: '100%' }}>
            {content}
        </Box>
    </Box>
);

export default GST;
